import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-10">
      <h2 className="font-serif text-2xl text-sage-800">{title}</h2>
      <div className="mt-4 space-y-4 text-sage-700/90">{children}</div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-6">
      <h3 className="font-serif text-lg text-sage-800">{title}</h3>
      <div className="mt-2 space-y-3 text-sage-700/90">{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        intro="How we collect, use, and protect information provided by visitors to this website."
      />

      <section className="bg-cream-50">
        <div className="section max-w-3xl">
          <p className="text-sm text-sage-600">Last Updated: April 17, 2026</p>

          <p className="mt-6 text-sage-700/90">
            This Privacy Policy explains how our dental practice ("we," "us," or "our") collects, uses, and protects
            information provided by visitors to this website. This policy applies only to information collected through
            this website and does not cover protected health information (PHI) handled by our practice, which is
            governed by our separate{" "}
            <Link to="/hipaa-policy" className="text-blush-500 underline hover:text-blush-600">
              HIPAA Notice of Privacy Practices
            </Link>
            . Your use of this website is also subject to our Terms &amp; Conditions.
          </p>
          <p className="mt-4 text-sage-700/90">
            By accessing or using this website, you consent to the practices described below.
          </p>

          <Section title="Information We Collect">
            <p>We collect two general categories of information:</p>
            <p>
              <strong>Information you provide voluntarily.</strong> When you contact us, request an appointment,
              complete a form, or subscribe to communications, you may provide your name, address, email address, phone
              number, and any details you choose to include in a message. Please do not submit sensitive medical or
              health information through general website forms.
            </p>
            <p>
              <strong>Information collected automatically.</strong> When you visit this website, we and our service
              providers automatically collect certain technical information, including your IP address, browser type,
              operating system, referring page, pages viewed, time spent on the site, and interactions with site
              content.
            </p>
          </Section>

          <Section title="How We Use Your Information">
            <p>
              We use the information we collect to respond to your inquiries, schedule appointments, provide information
              you request, improve our website and services, send communications you have opted into, and comply with
              legal obligations. We use your contact information for marketing only with your permission, and you may
              withdraw consent at any time.
            </p>
          </Section>

          <Section title="Cookies and Tracking Technologies">
            <p>
              This website uses cookies, pixels, and similar technologies to operate the site, remember preferences,
              analyze traffic, and (where applicable) support advertising. Some of these technologies are provided by
              third parties such as Google Analytics, which may receive identifiers associated with your device or
              browser.
            </p>
            <p>
              You can control cookies through your browser settings and, where offered, through a cookie preference tool
              on this website. Disabling cookies may limit some site functionality. We honor Global Privacy Control
              (GPC) signals where required by applicable law.
            </p>
          </Section>

          <Section title="How We Share Information">
            <p>We do <strong>not</strong> sell your personal information. We share information only in the following circumstances:</p>
            <p>
              <strong>Service providers.</strong> We share information with vendors who help operate our website,
              hosting, email, analytics, and communications, under agreements that require them to protect your
              information.
            </p>
            <p>
              <strong>Legal and safety.</strong> We may disclose information when required by law, in response to valid
              legal process, or to protect the rights, safety, or property of our practice, our patients, or others,
              including for fraud prevention or investigation.
            </p>
            <p>
              <strong>Business transfers.</strong> In the event of a merger, acquisition, or sale of assets,
              information may be transferred as part of that transaction.
            </p>
          </Section>

          <Section title="Data Security">
            <p>
              We use reasonable administrative, technical, and physical safeguards to protect information submitted
              through this website. Access is limited to authorized personnel. However, no method of transmission over
              the internet is completely secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="Data Retention">
            <p>
              We retain information for as long as needed to fulfill the purposes described in this policy, comply with
              legal obligations, resolve disputes, and enforce our agreements.
            </p>
          </Section>

          <Section title="Your Choices and Rights">
            <SubSection title="Email communications">
              <p>
                You can unsubscribe from marketing emails by using the unsubscribe link in any message or by contacting
                us using the information below.
              </p>
            </SubSection>
            <SubSection title="State privacy rights">
              <p>
                Depending on where you reside, you may have rights to access, correct, delete, or obtain a copy of
                personal information we hold about you, and to opt out of certain processing. Residents of California,
                Virginia, Colorado, Connecticut, Utah, Texas, and other states with comprehensive privacy laws may
                submit requests using the contact information below. We will respond within the timeframes required by
                applicable law and will not discriminate against you for exercising these rights.
              </p>
            </SubSection>
          </Section>

          <Section title="Children's Privacy">
            <p>
              This website is intended for a general audience and is not directed to children under 13. We do not
              knowingly collect personal information from children under 13 through this website. Parents or guardians
              who schedule appointments or provide information on behalf of a minor patient do so in their capacity as
              the child's legal representative. If you believe a child has provided information through this site,
              please contact us and we will delete it.
            </p>
          </Section>

          <Section title="Third-Party Links">
            <p>
              This website may contain links to third-party websites. We are not responsible for the privacy practices
              or content of those sites. We encourage you to review their privacy policies before providing any
              information.
            </p>
          </Section>

          <Section title="Medical Disclaimer">
            <p>
              This website provides general informational content only and does not establish a doctor-patient
              relationship. No specific medical, dental, or surgical advice, diagnosis, or treatment is offered through
              this site. Always consult a qualified healthcare provider regarding your specific condition or concerns.
            </p>
          </Section>

          <Section title="SMS Messaging Terms">
            <p>By opting in to receive SMS messages from us, you agree to the following terms:</p>
            <ul className="ml-4 mt-2 space-y-2">
              {[
                "We collect your phone number.",
                "You consent to receive SMS text messages from us for appointment reminders, marketing messages, and general two-way communication.",
                "Message frequency varies.",
                "Message and data rates may apply based on your mobile carrier's terms.",
                'You can opt out at any time by replying "STOP" to any SMS message. Reply "HELP" for support.',
                "By opting in, you confirm that you are the owner or authorized user of the phone number provided and that you are at least 18 years of age.",
              ].map((item) => (
                <li key={item} className="flex gap-2 text-sage-700/90">
                  <span aria-hidden="true" className="text-blush-400">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4">
              Mobile opt-in information and consent data will not be shared with third parties or affiliates for
              marketing or promotional purposes.
            </p>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the "Last Updated" date
              above. Material changes will be communicated by posting a notice on the website. We encourage you to
              review this policy periodically.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              If you have questions about this Privacy Policy or wish to exercise a privacy right, please{" "}
              <Link to="/#contact" className="text-blush-500 underline hover:text-blush-600">
                contact us
              </Link>
              .
            </p>
          </Section>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/hipaa-policy" className="btn-primary">
              View HIPAA Notice
            </Link>
            <Link to="/" className="btn-secondary">
              Back to home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
